// src/components/SOWDraftingModule.jsx — GoldenScope SOW Drafting Workspace
import { useState, useEffect, useRef } from 'react';
import supabase from '../utils/supabaseClient';

// ── Five W's config ────────────────────────────────────────────────────────
const W_ORDER = ['who', 'what', 'where', 'when', 'why', 'how'];
const W_META = {
  who:   { label: 'Who',   icon: '👤', section: 'SECTION 3 — CONTRACTOR REQUIREMENTS',    color: '#7c3aed' },
  what:  { label: 'What',  icon: '📋', section: 'SECTION 2 — SCOPE OF SERVICES',           color: '#2563eb' },
  where: { label: 'Where', icon: '📍', section: 'SECTION 4 — PLACE OF PERFORMANCE',        color: '#0891b2' },
  when:  { label: 'When',  icon: '📅', section: 'SECTION 5 — PERFORMANCE PERIOD',          color: '#d97706' },
  why:   { label: 'Why',   icon: '💡', section: 'SECTION 1 — BACKGROUND AND PURPOSE',      color: '#dc2626' },
  how:   { label: 'How',   icon: '⚙️', section: 'SECTION 6 — PERFORMANCE STANDARDS',       color: '#16a34a' },
};

// ── Penny system prompts ───────────────────────────────────────────────────
function buildInterviewSystem(agencyName, initialIdea, fiveWs) {
  const answered = Object.entries(fiveWs).filter(([, v]) => v).map(([k]) => k);
  const remaining = W_ORDER.filter(w => !answered.includes(w));
  return `You are Penny, an AI assistant inside GoldenScope helping a California state Contract Manager draft a Statement of Work (SOW) for Exhibit A.

You are conducting a structured Five W's + How interview. Respond ONLY with valid JSON — no markdown, no text outside the JSON object.

Response format:
{
  "message": "Your warm, professional response. Acknowledge the CM's answer briefly. Include inline SCM citations when relevant (e.g., per SCM Vol 1, Section 6.3...). End with your next question.",
  "draftSection": "who|what|where|when|why|how",
  "draftContent": "2-4 sentences of polished, formal SOW language for this section extracted from the CM's answer.",
  "scmCitation": "SCM Vol 1, Section X.X — brief description",
  "currentW": "who|what|where|when|why|how|complete"
}

Interview order (remaining): ${remaining.join(' → ') || 'all answered — set currentW to complete'}
Already answered: ${answered.join(', ') || 'none yet'}

SCM Guidance:
- WHO: Contractor type, required licenses/certifications, subcontracting. (SCM Vol 1, §6.3)
- WHAT: Services, deliverables, contractor responsibilities, materials. (SCM Vol 1, §6.4)
- WHERE: Specific locations — addresses, cities, counties, districts. (SCM Vol 1, §6.5)
- WHEN: Start date, end date, performance period, key milestones. (SCM Vol 1, §6.6)
- WHY: Business justification — why state personnel cannot fulfill the need. (SCM Vol 1, §3.1)
- HOW: Methods, standards, specifications, reporting requirements, metrics. (SCM Vol 1, §6.7)

Agency: ${agencyName || 'California State Agency'}
CM's initial idea: ${initialIdea}
Current SOW answers: ${JSON.stringify(fiveWs)}

After the CM answers the last remaining W (how), set currentW to "complete".`;
}

function buildComplianceSystem(agencyName, fiveWs, draftContent) {
  return `You are Penny reviewing a Statement of Work for Exhibit A compliance under the California State Contracting Manual.

Respond ONLY with valid JSON — no markdown, no text outside the JSON.

{
  "message": "Your compliance review. Start with 1 sentence of what is strong. Then describe any gaps clearly. Be specific and cite SCM sections. End encouragingly.",
  "isCompliant": true,
  "complianceNotes": [
    {
      "severity": "major|minor",
      "section": "who|what|where|when|why|how",
      "issue": "What is missing or unclear",
      "scmRef": "SCM Vol 1, Section X.X",
      "recommendation": "Specific fix"
    }
  ],
  "clarifyingQuestions": ["Question 1 if needed", "Question 2 if needed"]
}

Maximum 3 clarifying questions. Only include if there are genuine compliance gaps.

Compliance checklist:
- WHO: Is the contractor type specific? Are license requirements stated? (SCM Vol 1, §6.3)
- WHAT: Are deliverables measurable? Are responsibilities clearly defined? (SCM Vol 1, §6.4)
- WHERE: Is performance location specific enough (city/county/address)? (SCM Vol 1, §6.5)
- WHEN: Are start/end dates or period defined? Any milestones? (SCM Vol 1, §6.6)
- WHY: Is the business justification sufficient? (SCM Vol 1, §3.1)
- HOW: Are performance standards or reporting requirements included? (SCM Vol 1, §6.7)

Agency: ${agencyName}
Five W answers: ${JSON.stringify(fiveWs)}
Current draft: ${JSON.stringify(draftContent)}`;
}

function buildAskPennySystem(agencyName) {
  return `You are Penny, an AI assistant inside GoldenScope for California state Contract Managers. The CM's message will include the current SOW draft context (title, five W's answered so far, draft content, SCM citations) and any supporting documents they've uploaded in this session — you DO have access to this, so never say you lack memory of the draft or can't see uploaded documents; just use what's provided. Answer the CM's question directly, helpfully, and concisely, referencing the actual draft content and documents where relevant. Reference specific SCM sections when relevant. After answering, offer in one sentence to continue the SOW interview when they are ready. Keep your total response under 150 words. Agency: ${agencyName || 'California State Agency'}.`;
}

// ── PDF Export ─────────────────────────────────────────────────────────────
function exportToPDF(draft, draftContent, fiveWs, agencyName) {
  const ref = draft?.reference_number || '';
  const title = draft?.title || 'Statement of Work';
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const section = (num, heading, content) => content ? `
    <div class="sow-section">
      <div class="sow-section-header">SECTION ${num} — ${heading}</div>
      <div class="sow-section-body">${(content || '').replace(/\n/g, '<br>')}</div>
    </div>` : '';

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; color: #000; background: #fff; }
    .page { width: 8.5in; min-height: 11in; padding: 1in; margin: 0 auto; }
    .sow-header { border-bottom: 3px double #000; padding-bottom: 14px; margin-bottom: 20px; }
    .sow-title { font-size: 16pt; font-weight: bold; text-align: center; margin-bottom: 6px; }
    .sow-subtitle { font-size: 11pt; text-align: center; color: #333; margin-bottom: 8px; }
    .sow-meta { display: flex; justify-content: space-between; font-size: 9pt; color: #555; margin-top: 8px; }
    .ref-badge { font-family: monospace; font-size: 10pt; font-weight: bold; background: #f5f5f5; border: 1px solid #ccc; padding: 2px 8px; }
    .sow-section { margin-bottom: 20px; }
    .sow-section-header { font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: .06em; border-bottom: 1.5px solid #000; padding-bottom: 4px; margin-bottom: 8px; }
    .sow-section-body { font-size: 11pt; line-height: 1.8; text-align: justify; }
    .footer { border-top: 1px solid #ccc; margin-top: 40px; padding-top: 10px; font-size: 8pt; color: #888; display: flex; justify-content: space-between; }
    @media print { .page { margin: 0; } }
  </style>
  </head><body><div class="page">
  <div class="sow-header">
    <div class="sow-title">STATEMENT OF WORK</div>
    <div class="sow-subtitle">Exhibit A — ${agencyName || 'California State Agency'}</div>
    <div class="sow-meta">
      <span class="ref-badge">${ref}</span>
      <span>${title}</span>
      <span>Date: ${today}</span>
    </div>
  </div>
  ${section(1, 'BACKGROUND AND PURPOSE', draftContent.why || fiveWs.why)}
  ${section(2, 'SCOPE OF SERVICES', draftContent.what || fiveWs.what)}
  ${section(3, 'CONTRACTOR REQUIREMENTS', draftContent.who || fiveWs.who)}
  ${section(4, 'PLACE OF PERFORMANCE', draftContent.where || fiveWs.where)}
  ${section(5, 'PERFORMANCE PERIOD', draftContent.when || fiveWs.when)}
  ${section(6, 'PERFORMANCE STANDARDS AND METHODS', draftContent.how || fiveWs.how)}
  <div class="footer">
    <span>${ref} · DRAFT — NOT FOR EXECUTION</span>
    <span>Generated by GoldenScope · Powered by Penny</span>
    <span>${today}</span>
  </div>
  </div></body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 500);
}

// ── Component ──────────────────────────────────────────────────────────────
export default function SOWDraftingModule({ draft: initialDraft, profile, cmProfile, apiKey, callAI, onBack, onDraftSaved }) {
  // ── State ────────────────────────────────────────────────────────────
  const [draft, setDraft]             = useState(initialDraft || null);
  const [phase, setPhase]             = useState(() => {
    if (!initialDraft) return 'idea';
    const status = initialDraft.status;
    if (status === 'complete') return 'complete';
    if (status === 'reviewing') return 'reviewing';
    // Check if all W's answered — if so, go to reviewing
    const saved = initialDraft?.five_ws || {};
    const allAnswered = W_ORDER.every(w => saved[w]);
    if (allAnswered && (initialDraft.compliance_notes?.length >= 0)) return 'reviewing';
    return 'interview';
  });
  const [messages, setMessages]       = useState([]);
  const [draftContent, setDraftContent] = useState(initialDraft?.draft_content || {});
  const [fiveWs, setFiveWs]           = useState(initialDraft?.five_ws || {});
  const [currentW, setCurrentW]       = useState(() => {
    // On load, find the first unanswered W from saved data
    const saved = initialDraft?.five_ws || {};
    return W_ORDER.find(w => !saved[w]) || 'complete';
  });
  const [scmCitations, setScmCitations] = useState(initialDraft?.scm_citations || []);
  const [complianceNotes, setComplianceNotes] = useState(initialDraft?.compliance_notes || []);
  const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
  const [clarifyingAnswers, setClarifyingAnswers] = useState([]);

  const [uploadMode, setUploadMode]       = useState('describe'); // 'describe'|'upload'
  const [uploadFile, setUploadFile]       = useState(null);       // { name, base64 }
  const [uploadIntent, setUploadIntent]   = useState('autocomplete'); // 'autocomplete'|'startingpoint'
  const [uploadLoading, setUploadLoading] = useState(false);
  const [initialIdea, setInitialIdea]     = useState(initialDraft?.initial_idea || '');
  const [input, setInput]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [noteStatuses, setNoteStatuses]   = useState(() => {
    // Restore note statuses from saved compliance_notes
    const saved = initialDraft?.compliance_notes || [];
    const statuses = {};
    saved.forEach((note, i) => {
      if (note.status) statuses[i] = note.status;
    });
    return statuses;
  }); // { index: 'open'|'acknowledged'|'resolved' }
  const [noteInputs, setNoteInputs]       = useState({}); // { index: text }
  const [noteLoading, setNoteLoading]     = useState({}); // { index: bool }
  const [askPennyOpen, setAskPennyOpen]   = useState(false);
  const [askPennyInput, setAskPennyInput] = useState('');
  const [askPennyMessages, setAskPennyMessages] = useState([]);
  const [askPennyLoading, setAskPennyLoading] = useState(false);
  const [title, setTitle]               = useState(initialDraft?.title || '');
  const [estimatedBudget, setEstimatedBudget] = useState(initialDraft?.estimated_budget || '');
  const [budgetPhase, setBudgetPhase]     = useState(false); // true when in budget question
  const [supportingDocs, setSupportingDocs] = useState(initialDraft?.supporting_docs || []);
  const [docsPhase, setDocsPhase]         = useState(false); // true when in docs upload step
  const [docUploadLoading, setDocUploadLoading] = useState(false);

  const messagesEndRef   = useRef(null);
  const askPennyEndRef   = useRef(null);
  const agencyName = cmProfile?.agency_name || profile?.agencyName || 'California State Agency';
  const agencyCode = (agencyName.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10)) || 'CA';

  // ── Scroll to bottom on new messages ─────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    askPennyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [askPennyMessages]);

  // ── Load existing draft messages ──────────────────────────────────────
  useEffect(() => {
    if (initialDraft?.id) loadMessages(initialDraft.id);
  }, [initialDraft?.id]); // eslint-disable-line

  async function loadMessages(draftId) {
    const { data } = await supabase
      .from('sow_messages')
      .select('*')
      .eq('sow_draft_id', draftId)
      .order('created_at', { ascending: true });
    if (data?.length) {
      // Split ask_penny messages into their own panel — never mix with main chat
      const mainMsgs = data.filter(m => m.message_type !== 'ask_penny');
      const askPennyMsgs = data.filter(m => m.message_type === 'ask_penny');

      setMessages(mainMsgs.map(m => ({
        role: m.role, content: m.content,
        type: m.message_type, metadata: m.metadata || {},
      })));

      // Restore ask penny conversation
      if (askPennyMsgs.length > 0) {
        setAskPennyMessages(askPennyMsgs.map(m => ({
          role: m.role, content: m.content,
        })));
      }

      // Restore phase from last message type
      const lastMsg = mainMsgs[mainMsgs.length - 1];
      if (lastMsg?.message_type === 'compliance') {
        setPhase('reviewing');
      } else if (lastMsg?.message_type === 'clarification') {
        setPhase('clarifying');
      }

      // Restore currentW — find next unanswered W from saved five_ws
      const savedFiveWs = initialDraft?.five_ws || {};
      const nextUnanswered = W_ORDER.find(w => !savedFiveWs[w]);
      if (nextUnanswered) {
        setCurrentW(nextUnanswered);
      } else {
        setCurrentW('complete');
      }
    }
  }

  // ── Save draft to Supabase ────────────────────────────────────────────
  async function saveDraft(updates) {
    if (!draft?.id) return;
    setSaving(true);
    try {
      await supabase.from('sow_drafts').update({
        ...updates,
        updated_at: new Date().toISOString(),
      }).eq('id', draft.id);
      setDraft(d => ({ ...d, ...updates }));
      if (onDraftSaved) onDraftSaved({ ...draft, ...updates });
    } catch (e) { console.error('saveDraft error:', e); }
    finally { setSaving(false); }
  }

  // ── Save message to Supabase ──────────────────────────────────────────
  async function saveMessage(role, content, type, metadata = {}) {
    if (!draft?.id) return;
    await supabase.from('sow_messages').insert({
      sow_draft_id: draft.id,
      role, content,
      message_type: type,
      metadata,
    });
  }

  // ── Handle file upload ───────────────────────────────────────────────
  async function handleFileUpload(file) {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setUploadFile({ name: file.name, base64, size: file.size });
    };
    reader.readAsDataURL(file);
  }

  // ── Create draft from uploaded SOW ───────────────────────────────────
  async function createDraftFromUpload() {
    if (!uploadFile?.base64) return;
    setUploadLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data: prof } = await supabase
        .from('user_profiles').select('cm_profile_id, agency_id').eq('id', user.id).single();

      // Penny extracts Five W's from the uploaded document
      const extractSystem = uploadIntent === 'autocomplete'
        ? `You are Penny extracting a Statement of Work from an uploaded PDF. Extract all Five W's + How answers. Return ONLY valid JSON:
{
  "title": "Short SOW title",
  "initialIdea": "2-3 sentence summary of what this SOW is about",
  "who": "Contractor requirements extracted from document",
  "what": "Scope of services extracted",
  "where": "Place of performance extracted",
  "when": "Performance period extracted",
  "why": "Background and purpose extracted",
  "how": "Performance standards extracted",
  "confidence": { "who": 0-100, "what": 0-100, "where": 0-100, "when": 0-100, "why": 0-100, "how": 0-100 },
  "gaps": ["List any sections that were vague or missing"]
}`
        : `You are Penny analyzing a sample SOW that a Contract Manager wants to use as a starting point. Extract what you can, but flag areas they may want to modify. Return ONLY valid JSON:
{
  "title": "Short SOW title based on the sample",
  "initialIdea": "2-3 sentence summary noting this is adapted from an existing SOW",
  "who": "Contractor requirements from sample (CM may want to modify)",
  "what": "Scope of services from sample",
  "where": "Place of performance from sample",
  "when": "Performance period from sample",
  "why": "Background and purpose from sample",
  "how": "Performance standards from sample",
  "confidence": { "who": 0-100, "what": 0-100, "where": 0-100, "when": 0-100, "why": 0-100, "how": 0-100 },
  "suggestedModifications": ["Areas the CM may want to review and update for their specific needs"]
}`;

      const raw = await callAI(apiKey, {
        system: extractSystem,
        messages: [{ role: 'user', content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: uploadFile.base64 } },
          { type: 'text', text: uploadIntent === 'autocomplete'
            ? "Please extract all Five W's from this SOW document."
            : "Please extract the Five W's from this sample SOW. I want to use it as a starting point and will modify it." },
        ]}],
        max_tokens: 1200,
        tag: 'sow_extract_from_upload',
      });

      const extracted = JSON.parse(raw.trim().replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim());

      // Generate reference number
      const { data: refData } = await supabase.rpc('generate_sow_reference', { agency_code: agencyCode });
      const refNum = refData || ('GS-SOW-' + new Date().getFullYear() + '-' + agencyCode + '-0001');

      const sowTitle = extracted.title || uploadFile.name.replace(/\.pdf$/i, '');
      setTitle(sowTitle);

      const newFiveWs = {
        who: extracted.who || '', what: extracted.what || '',
        where: extracted.where || '', when: extracted.when || '',
        why: extracted.why || '', how: extracted.how || '',
      };
      const newDraftContent = { ...newFiveWs };
      setFiveWs(newFiveWs);
      setDraftContent(newDraftContent);
      setInitialIdea(extracted.initialIdea || '');

      // Create draft record
      const { data: newDraft, error } = await supabase.from('sow_drafts').insert({
        reference_number: refNum,
        cm_profile_id: prof?.cm_profile_id || null,
        cm_user_id: user.id,
        agency_id: prof?.agency_id || null,
        agency_name: agencyName,
        title: sowTitle,
        initial_idea: extracted.initialIdea || '',
        status: 'drafting',
        draft_content: newDraftContent,
        five_ws: newFiveWs,
        compliance_notes: [],
        scm_citations: [],
        pain_points: extracted.gaps || extracted.suggestedModifications || [],
      }).select().single();

      if (error) throw error;
      setDraft(newDraft);
      setPhase('interview');

      // Post Penny's extraction summary
      const confidence = extracted.confidence || {};
      const lowConf = Object.entries(confidence).filter(([, v]) => v < 70).map(([k]) => k.toUpperCase());
      const gaps = extracted.gaps || extracted.suggestedModifications || [];

      const sectionSummary = W_ORDER.map(w => {
        const conf = confidence[w] || 100;
        const found = newFiveWs[w] ? 'Extracted' : 'Not found';
        const lowConf = conf < 70 ? ' (low confidence — please review)' : '';
        return W_META[w].icon + ' ' + W_META[w].label + ': ' + found + lowConf;
      }).join('\n');

      const lowConfSections = Object.entries(confidence).filter(([,v]) => v < 70).map(([k]) => k.toUpperCase());
      const gapsList = (extracted.gaps || extracted.suggestedModifications || []).slice(0, 3).join('; ');

      let introContent;
      if (uploadIntent === 'autocomplete') {
        introContent = "I've read through your uploaded SOW and pulled all six sections. Here's what I was able to extract:\n\n" + sectionSummary;
        if (lowConfSections.length > 0) introContent += '\n\n⚠ A few sections had lower confidence — ' + lowConfSections.join(', ') + '. I\'ve flagged them in the right panel so you can review and fill in any gaps.';
        if (gapsList) introContent += '\n\nI also noted: ' + gapsList;
        introContent += "\n\nI'm running a full compliance review now — I'll flag anything that needs attention before this goes further. Review the extracted content in the right panel and make any edits, then we'll be ready to generate your Exhibit A.";
      } else {
        introContent = "I've reviewed your sample SOW and used it as a starting point. Here's what I extracted:\n\n" + sectionSummary;
        const mods = (extracted.suggestedModifications || []).slice(0, 4).map(g => '• ' + g).join('\n');
        if (mods) introContent += '\n\nA few things worth adapting for your contract:\n' + mods;
        introContent += "\n\nReview each section in the right panel and edit anything that doesn't fit your specific contract. Once you're happy with the content, I'll run a compliance review and we'll generate the Exhibit A.\n\nAsk me anything along the way using the Ask Penny button.";
      }
      // Set currentW to complete since all sections are pre-filled, then run compliance
      setCurrentW('complete');
      setTimeout(() => runComplianceReview(newFiveWs, newDraftContent), 1500);

    } catch (e) {
      console.error('createDraftFromUpload error:', e);
      alert('Failed to process upload: ' + e.message);
    } finally {
      setUploadLoading(false);
    }
  }

  // ── Create new draft ──────────────────────────────────────────────────
  async function createDraft() {
    if (!initialIdea.trim()) return;
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: prof } = await supabase
        .from('user_profiles').select('cm_profile_id, agency_id').eq('id', user.id).single();

      // Generate reference number
      const { data: refData } = await supabase.rpc('generate_sow_reference', { agency_code: agencyCode });
      const refNum = refData || ('GS-SOW-' + new Date().getFullYear() + '-' + agencyCode + '-0001');

      // Generate title from idea using Penny
      let draftTitle = initialIdea.slice(0, 60).trim();
      try {
        const t = await callAI(apiKey, {
          system: 'Generate a short 4-8 word title for this SOW based on the description. Return ONLY the title, no quotes, no punctuation at the end.',
          messages: [{ role: 'user', content: initialIdea }],
          max_tokens: 30,
          tag: 'sow_title_gen',
        });
        draftTitle = t.trim().replace(/['"]/g, '');
      } catch (_) {}
      setTitle(draftTitle);

      const { data: newDraft, error } = await supabase.from('sow_drafts').insert({
        reference_number: refNum,
        cm_profile_id: prof?.cm_profile_id || null,
        cm_user_id: user.id,
        agency_id: prof?.agency_id || null,
        agency_name: agencyName,
        title: draftTitle,
        initial_idea: initialIdea.trim(),
        status: 'drafting',
        draft_content: {},
        five_ws: {},
        compliance_notes: [],
        scm_citations: [],
        pain_points: [],
      }).select().single();

      if (error) throw error;
      setDraft(newDraft);
      setPhase('interview');

      // Kick off Penny's first message
      await startInterview(newDraft, initialIdea.trim());
    } catch (e) {
      console.error('createDraft error:', e);
      alert('Failed to create draft: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Start interview — Penny acknowledges idea and asks first W ────────
  async function startInterview(newDraft, idea) {
    // Show Five W's intro message immediately before first API call
    const introMsg = {
      role: 'assistant', type: 'interview', metadata: {},
      content: "Got it — I've read through what you've shared and I'm ready to help you build this out.\n\nHere's what to expect:\n\nI'll ask you a short series of focused questions — typically 6 to 8 — covering the key elements of a complete Scope of Work: who the contractor is, what they'll deliver, where and when work happens, why this contract is needed, and how performance will be measured.\n\nYour answers don't need to be perfect. Speak naturally — I'll handle the formal contract language. If you're not sure about something, just say so and I'll flag it or suggest standard language.\n\nOnce we've worked through the questions, I'll draft a complete Exhibit A SOW formatted to state contracting standards, ready for your review.\n\nA few things that will help:\n• The more specific you can be about deliverables and timelines, the stronger the SOW\n• If you have a similar past contract or a request document, you can upload it using the button in the top right\n• You can ask me a question at any point using the Ask Penny button — it won't interrupt the interview\n\nReady when you are — let's start with who this contract is for.",
    };
    setMessages([introMsg]);
    await supabase.from('sow_messages').insert({
      sow_draft_id: newDraft.id, role: 'assistant',
      content: introMsg.content, message_type: 'interview', metadata: {},
    });

    const system = buildInterviewSystem(agencyName, idea, {});
    const pennyRaw = await callAI(apiKey, {
      system,
      messages: [{ role: 'user', content: `Here is my initial idea for this SOW:\n\n"${idea}"\n\nBriefly acknowledge what I've shared in 1-2 sentences — confirm what you understand about the contract and note anything that will help make the SOW strong. Then ask your first question: WHO needs this service — what type of contractor or vendor will be performing this work? Keep your response conversational and under 100 words.` }],
      max_tokens: 600,
      tag: 'sow_interview_start',
    });

    const parsed = safeParse(pennyRaw);
    const pennyMsg = { role: 'assistant', content: parsed.message || pennyRaw, type: 'interview', metadata: { currentW: 'who', scmCitation: parsed.scmCitation } };
    setMessages([pennyMsg]);
    setCurrentW('who');
    await supabase.from('sow_messages').insert({
      sow_draft_id: newDraft.id, role: 'assistant',
      content: pennyMsg.content, message_type: 'interview',
      metadata: pennyMsg.metadata,
    });
  }

  // ── Send interview message ────────────────────────────────────────────
  async function sendInterviewMessage() {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');

    const userMsg = { role: 'user', content: userText, type: 'interview', metadata: {} };
    setMessages(p => [...p, userMsg]);
    await saveMessage('user', userText, 'interview');
    setLoading(true);

    try {
      const system = buildInterviewSystem(agencyName, draft?.initial_idea || initialIdea, fiveWs);
      const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));

      const pennyRaw = await callAI(apiKey, { system, messages: history, max_tokens: 700, tag: 'sow_interview_turn' });
      const parsed = safeParse(pennyRaw);

      // Update five W's and draft content
      const newFiveWs = { ...fiveWs };
      const newDraftContent = { ...draftContent };
      if (parsed.draftSection && parsed.draftContent) {
        newFiveWs[parsed.draftSection] = userText;
        newDraftContent[parsed.draftSection] = parsed.draftContent;
        setFiveWs(newFiveWs);
        setDraftContent(newDraftContent);
      }

      // Track SCM citations
      if (parsed.scmCitation) {
        const newCitations = [...scmCitations, { section: parsed.draftSection, citation: parsed.scmCitation }];
        setScmCitations(newCitations);
      }

      const pennyMsg = {
        role: 'assistant', content: parsed.message || pennyRaw,
        type: 'interview',
        metadata: { currentW: parsed.currentW, scmCitation: parsed.scmCitation, draftSection: parsed.draftSection },
      };
      setMessages(p => [...p, pennyMsg]);
      await saveMessage('assistant', pennyMsg.content, 'interview', pennyMsg.metadata);

      // Save updated draft
      await saveDraft({ five_ws: newFiveWs, draft_content: newDraftContent, scm_citations: scmCitations });

      // Advance W — use Penny's response as a guide but also track ourselves
      // If Penny says complete, or we've now answered all 6 W's, go to budget question first
      const allAnswered = W_ORDER.every(w => newFiveWs[w]);
      if (parsed.currentW === 'complete' || allAnswered) {
        setCurrentW('complete');
        setTimeout(() => askBudgetQuestion(newFiveWs, newDraftContent), 1200);
      } else {
        // Find the next unanswered W in order
        const nextUnanswered = W_ORDER.find(w => !newFiveWs[w]);
        if (nextUnanswered) {
          setCurrentW(nextUnanswered);
        } else if (parsed.currentW && W_ORDER.includes(parsed.currentW)) {
          setCurrentW(parsed.currentW);
        }
      }
    } catch (e) {
      const errMsg = { role: 'assistant', content: '⚠ ' + e.message, type: 'interview', metadata: {} };
      setMessages(p => [...p, errMsg]);
    } finally {
      setLoading(false);
    }
  }

  // ── Compliance review ─────────────────────────────────────────────────
  async function runComplianceReview(ws, dc) {
    setPhase('compliance');
    setLoading(true);

    const transitionMsg = {
      role: 'assistant', type: 'compliance', metadata: {},
      content: "Great work! You've answered all six W's. Let me do a full compliance review of your SOW draft before we finalize it...",
    };
    setMessages(p => [...p, transitionMsg]);
    await saveMessage('assistant', transitionMsg.content, 'compliance');

    try {
      const system = buildComplianceSystem(agencyName, ws || fiveWs, dc || draftContent);
      const pennyRaw = await callAI(apiKey, {
        system,
        messages: [{ role: 'user', content: 'Please review my complete SOW draft for Exhibit A compliance.' }],
        max_tokens: 1000,
        tag: 'sow_compliance_review',
      });
      const parsed = safeParse(pennyRaw);

      if (parsed.complianceNotes?.length) setComplianceNotes(parsed.complianceNotes);

      const reviewMsg = { role: 'assistant', content: parsed.message || pennyRaw, type: 'compliance', metadata: { isCompliant: parsed.isCompliant } };
      setMessages(p => [...p, reviewMsg]);
      await saveMessage('assistant', reviewMsg.content, 'compliance', reviewMsg.metadata);
      await saveDraft({ compliance_notes: parsed.complianceNotes || [], status: 'reviewing' });

      if (parsed.clarifyingQuestions?.length) {
        setClarifyingQuestions(parsed.clarifyingQuestions);
        setPhase('clarifying');
        // Ask first clarifying question
        const qMsg = {
          role: 'assistant', type: 'clarification', metadata: { questionIndex: 0 },
          content: `I have ${parsed.clarifyingQuestions.length} clarifying question${parsed.clarifyingQuestions.length > 1 ? 's' : ''} to strengthen your SOW:\n\n**Question 1:** ${parsed.clarifyingQuestions[0]}`,
        };
        setMessages(p => [...p, qMsg]);
        await saveMessage('assistant', qMsg.content, 'clarification', qMsg.metadata);
      } else {
        // No clarifying needed — complete!
        await completeDraft();
      }
    } catch (e) {
      const errMsg = { role: 'assistant', content: '⚠ Compliance review error: ' + e.message, type: 'compliance', metadata: {} };
      setMessages(p => [...p, errMsg]);
    } finally {
      setLoading(false);
    }
  }

  // ── Handle clarifying answer ──────────────────────────────────────────
  async function sendClarifyingAnswer() {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');

    const answeredCount = clarifyingAnswers.length;
    const userMsg = { role: 'user', content: userText, type: 'clarification', metadata: { questionIndex: answeredCount } };
    setMessages(p => [...p, userMsg]);
    await saveMessage('user', userText, 'clarification', { questionIndex: answeredCount });

    const newAnswers = [...clarifyingAnswers, userText];
    setClarifyingAnswers(newAnswers);
    setLoading(true);

    try {
      // Update draft based on clarifying answer
      const updateSystem = `You are Penny. The CM answered a clarifying question about their SOW. Update the relevant SOW section based on their answer. Respond ONLY with JSON:
{"draftSection":"who|what|where|when|why|how","updatedContent":"Updated SOW language for this section","message":"Brief acknowledgment of their answer (1-2 sentences)"}`;
      const raw = await callAI(apiKey, {
        system: updateSystem,
        messages: [{ role: 'user', content: `Question: ${clarifyingQuestions[answeredCount]}\nAnswer: ${userText}\nCurrent draft: ${JSON.stringify(draftContent)}` }],
        max_tokens: 400,
        tag: 'sow_clarification_answer',
      });
      const parsed = safeParse(raw);
      let updatedNotes = [...complianceNotes];
      let updatedStatuses = { ...noteStatuses };

      if (parsed.draftSection && parsed.updatedContent) {
        const newDC = { ...draftContent, [parsed.draftSection]: parsed.updatedContent };
        setDraftContent(newDC);

        // Re-evaluate any open compliance note for this section
        const noteIndex = complianceNotes.findIndex(
          n => n.section === parsed.draftSection &&
          (noteStatuses[complianceNotes.indexOf(n)] || n.status || 'open') === 'open'
        );

        if (noteIndex >= 0 && parsed.updatedContent) {
          try {
            const note = complianceNotes[noteIndex];
            const reEvalSystem = `You are Penny re-evaluating a compliance note after the CM provided additional information.
Respond ONLY with JSON: {"resolved": true/false, "newSeverity": "major|minor|resolved", "reason": "1 sentence"}`;
            const reEvalRaw = await callAI(apiKey, {
              system: reEvalSystem,
              messages: [{ role: 'user', content: 'Original issue: ' + note.issue + '\nSCM ref: ' + (note.scmRef || '') + '\nCM clarifying answer: ' + userText + '\nUpdated section content: ' + parsed.updatedContent }],
              max_tokens: 150,
              tag: 'sow_compliance_reeval',
            });
            const reEval = safeParse(reEvalRaw);
            if (reEval.newSeverity) {
              const newStatus = reEval.resolved ? 'resolved' : 'open';
              updatedNotes = complianceNotes.map((n, i) =>
                i === noteIndex ? { ...n, severity: reEval.newSeverity, status: newStatus } : n
              );
              updatedStatuses = { ...noteStatuses, [noteIndex]: newStatus };
              setComplianceNotes(updatedNotes);
              setNoteStatuses(updatedStatuses);
            }
          } catch (e) { console.warn('Note re-eval error:', e); }
        }

        await saveDraft({ draft_content: newDC, compliance_notes: updatedNotes });
      } else {
        await saveDraft({ draft_content: draftContent });
      }

      if (newAnswers.length < clarifyingQuestions.length) {
        // Ask next clarifying question
        const nextQ = clarifyingQuestions[newAnswers.length];
        const nextMsg = {
          role: 'assistant', type: 'clarification',
          metadata: { questionIndex: newAnswers.length },
          content: `${parsed.message || 'Got it!'}\n\n**Question ${newAnswers.length + 1}:** ${nextQ}`,
        };
        setMessages(p => [...p, nextMsg]);
        await saveMessage('assistant', nextMsg.content, 'clarification', nextMsg.metadata);
      } else {
        // All clarifying questions answered
        const doneMsg = { role: 'assistant', content: parsed.message || "Perfect — I've updated your SOW with your answers.", type: 'clarification', metadata: {} };
        setMessages(p => [...p, doneMsg]);
        await saveMessage('assistant', doneMsg.content, 'clarification');
        await completeDraft();
      }
    } catch (e) {
      const errMsg = { role: 'assistant', content: '⚠ ' + e.message, type: 'clarification', metadata: {} };
      setMessages(p => [...p, errMsg]);
    } finally {
      setLoading(false);
    }
  }

  // ── Note status helpers ──────────────────────────────────────────────
  function acknowledgeNote(index) {
    const updated = { ...noteStatuses, [index]: 'acknowledged' };
    setNoteStatuses(updated);
    // Persist to draft
    const updatedNotes = complianceNotes.map((n, i) =>
      i === index ? { ...n, status: 'acknowledged' } : n
    );
    setComplianceNotes(updatedNotes);
    saveDraft({ compliance_notes: updatedNotes });
  }

  async function resolveNote(index) {
    const userText = noteInputs[index]?.trim();
    if (!userText || noteLoading[index]) return;
    setNoteLoading(p => ({ ...p, [index]: true }));

    const note = complianceNotes[index];
    try {
      const system = `You are Penny reviewing a compliance fix for a California state SOW. The CM is addressing a flagged issue. Evaluate if their response resolves it.
Respond ONLY with JSON:
{
  "resolved": true/false,
  "newSeverity": "major|minor|resolved",
  "updatedContent": "Updated SOW language for the ${note.section} section if content improved",
  "message": "1-2 sentence response acknowledging their fix and noting if it resolved the issue"
}`;

      const raw = await callAI(apiKey, {
        system,
        messages: [{ role: 'user', content: `Original issue: ${note.issue}\nSCM reference: ${note.scmRef || ''}\nCM response: ${userText}\nCurrent draft for this section: ${draftContent[note.section] || fiveWs[note.section] || 'Not drafted'}` }],
        max_tokens: 300,
        tag: 'sow_compliance_response',
      });
      const parsed = safeParse(raw);

      // Update note status
      const newStatus = parsed.resolved ? 'resolved' : 'open';
      const updatedNotes = complianceNotes.map((n, i) =>
        i === index ? { ...n, severity: parsed.newSeverity || n.severity, status: newStatus, cmResponse: userText } : n
      );
      setComplianceNotes(updatedNotes);
      setNoteStatuses(p => ({ ...p, [index]: newStatus }));
      setNoteInputs(p => ({ ...p, [index]: '' }));

      // Update draft content if Penny improved it
      if (parsed.updatedContent && note.section) {
        const newDC = { ...draftContent, [note.section]: parsed.updatedContent };
        setDraftContent(newDC);
        await saveDraft({ draft_content: newDC, compliance_notes: updatedNotes });
      } else {
        await saveDraft({ compliance_notes: updatedNotes });
      }

      // Post Penny's response to main chat
      const pennyMsg = {
        role: 'assistant', type: 'compliance', metadata: {},
        content: parsed.message || (parsed.resolved ? '✅ Issue resolved — SOW updated.' : '⚠ More detail needed to fully resolve this.'),
      };
      setMessages(p => [...p, pennyMsg]);
      await saveMessage('assistant', pennyMsg.content, 'compliance');

    } catch (e) {
      console.error('resolveNote error:', e);
    } finally {
      setNoteLoading(p => ({ ...p, [index]: false }));
    }
  }

  // ── Check if all notes addressed (for export gate) ────────────────────
  function allNotesAddressed() {
    if (complianceNotes.length === 0) return true;
    return complianceNotes.every((_, i) => {
      const status = noteStatuses[i] || complianceNotes[i].status || 'open';
      return status === 'resolved' || status === 'acknowledged';
    });
  }

  // ── Budget question ─────────────────────────────────────────────────
  async function askBudgetQuestion(ws, dc) {
    setBudgetPhase(true);
    const budgetMsg = {
      role: 'assistant', type: 'interview', metadata: { isBudget: true },
      content: "Great work — we've covered all six sections of your SOW!\n\nOne last question before the compliance review: what is your estimated budget or approved not-to-exceed amount for this contract?\n\nYour best estimate is fine if you don't have an exact figure. This stays internal and won't appear in any public solicitation documents — it helps your analyst determine the appropriate solicitation type.",
    };
    setMessages(p => [...p, budgetMsg]);
    await saveMessage('assistant', budgetMsg.content, 'interview', { isBudget: true });
    // Store ws/dc for later use
    saveDraft({ five_ws: ws, draft_content: dc });
  }

  async function handleBudgetAnswer() {
    if (!input.trim() || loading) return;
    const budgetText = input.trim();
    setInput('');
    setBudgetPhase(false);
    setEstimatedBudget(budgetText);

    const userMsg = { role: 'user', content: budgetText, type: 'interview', metadata: { isBudget: true } };
    setMessages(p => [...p, userMsg]);
    await saveMessage('user', budgetText, 'interview', { isBudget: true });
    await saveDraft({ estimated_budget: budgetText });

    // Acknowledge and move to supporting docs
    const ackMsg = {
      role: 'assistant', type: 'interview', metadata: {},
      content: `Got it — estimated budget of **${budgetText}** noted.

Before I run the compliance review, do you have any supporting documents that could help your analyst or the eventual contractor? Things like technical specifications, process documents, user manuals, contact lists, performance standards, or any other reference materials relevant to this contract.

You can upload as many as you'd like, or skip this step if you don't have anything to add right now.`,
    };
    setMessages(p => [...p, ackMsg]);
    await saveMessage('assistant', ackMsg.content, 'interview', {});
    setDocsPhase(true);
  }

  // ── Supporting docs upload ────────────────────────────────────────
  const DOC_TAGS = [
    'Technical Specification', 'Performance Standards', 'Process Document',
    'User Manual', 'Contact List', 'Reference Material', 'Other',
  ];

  async function uploadSupportingDoc(file) {
    setDocUploadLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        let storagePath = null;
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user && draft?.id) {
            const path = user.id + '/' + draft.id + '/supporting/' + file.name.replace(/\s+/g, '_');
            const bytes = atob(base64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blob = new Blob([arr], { type: 'application/pdf' });
            const { error } = await supabase.storage
              .from('exhibit-files')
              .upload(path, blob, { upsert: true });
            if (!error) storagePath = path;
          }
        } catch (e) { console.warn('Supporting doc upload error:', e); }

        const newDoc = {
          id: Date.now().toString(),
          name: file.name.replace(/\.pdf$/i, ''),
          fileName: file.name,
          size: file.size,
          tag: 'Other',
          includeInContract: false,
          storagePath,
          base64: storagePath ? null : base64,
          uploadedAt: new Date().toISOString(),
        };
        const updated = [...supportingDocs, newDoc];
        setSupportingDocs(updated);
        await saveDraft({ supporting_docs: updated });
      };
      reader.readAsDataURL(file);
    } finally {
      setDocUploadLoading(false);
    }
  }

  function updateSupportingDoc(id, updates) {
    const updated = supportingDocs.map(d => d.id === id ? { ...d, ...updates } : d);
    setSupportingDocs(updated);
    saveDraft({ supporting_docs: updated });
  }

  function removeSupportingDoc(id) {
    const updated = supportingDocs.filter(d => d.id !== id);
    setSupportingDocs(updated);
    saveDraft({ supporting_docs: updated });
  }

  async function proceedToCompliance() {
    setDocsPhase(false);
    const docsCount = supportingDocs.length;
    if (docsCount > 0) {
      const docsMsg = {
        role: 'assistant', type: 'interview', metadata: {},
        content: `${docsCount} supporting document${docsCount > 1 ? 's' : ''} captured — your analyst will have access to ${docsCount > 1 ? 'all of them' : 'it'} when they open this SOW.

Now let me run the full compliance review...`,
      };
      setMessages(p => [...p, docsMsg]);
      await saveMessage('assistant', docsMsg.content, 'interview', {});
    }
    setTimeout(() => runComplianceReview(fiveWs, draftContent), 800);
  }

  // ── Complete draft ────────────────────────────────────────────────────
  async function completeDraft() {
    // Show compliance review phase — CM must address notes before completing
    setPhase('reviewing');
    await saveDraft({ status: 'reviewing' });

    const acknowledged = complianceNotes.filter((_, i) => (noteStatuses[i] || complianceNotes[i]?.status) === 'acknowledged').length;
    const noteCount = complianceNotes.length;

    const reviewMsg = {
      role: 'assistant', type: 'compliance', metadata: {},
      content: noteCount === 0
        ? `✅ No compliance issues found! Your SOW looks great.\n\nReview the draft on the right, then click **"Finalize SOW"** when you are ready to export.`
        : `Your SOW draft is ready for your review.\n\nI found **${noteCount} compliance note${noteCount > 1 ? 's' : ''}** in the right panel. Please review each one — you can provide additional information to resolve them, or acknowledge them to continue.\n\nOnce all notes are addressed, the export button will unlock.`,
    };
    setMessages(p => [...p, reviewMsg]);
    await saveMessage('assistant', reviewMsg.content, 'compliance', {});
  }

  async function finalizeSOW() {
    setPhase('complete');
    await saveDraft({ status: 'complete' });
    const acknowledged = complianceNotes.filter((_, i) => (noteStatuses[i] || complianceNotes[i]?.status) === 'acknowledged').length;
    const resolved = complianceNotes.filter((_, i) => (noteStatuses[i] || complianceNotes[i]?.status) === 'resolved').length;
    const completeMsg = {
      role: 'assistant', type: 'compliance', metadata: { isComplete: true },
      content: `✅ Your SOW is complete and ready to export!\n\nReference: **${draft?.reference_number}**\n\n${resolved > 0 ? `${resolved} issue${resolved > 1 ? 's' : ''} resolved. ` : ''}${acknowledged > 0 ? `${acknowledged} item${acknowledged > 1 ? 's' : ''} acknowledged for analyst review. ` : ''}\n\nDownload your SOW below. Your contract analyst can enter the reference number to receive a full briefing on this session.`,
    };
    setMessages(p => [...p, completeMsg]);
    await saveMessage('assistant', completeMsg.content, 'compliance', { isComplete: true });
  }

  // ── Ask Penny slide-out (freeform) ───────────────────────────────────
  async function sendAskPenny() {
    if (!askPennyInput.trim() || askPennyLoading) return;
    const userText = askPennyInput.trim();
    setAskPennyInput('');

    const userMsg = { role: 'user', content: userText };
    setAskPennyMessages(p => [...p, userMsg]);
    setAskPennyLoading(true);

    try {
      // Save to sow_messages with ask_penny type
      await saveMessage('user', userText, 'ask_penny');

      // Build real context so Penny isn't starting from a blank slate —
      // the current draft, five W's, SCM citations, and any supporting
      // documents uploaded in this session.
      const answeredWs = W_ORDER.filter(w => fiveWs[w]).map(w => w + ': ' + fiveWs[w]).join(' | ');
      const draftContentText = Object.keys(draftContent).length
        ? Object.entries(draftContent).map(([k, v]) => k.toUpperCase() + ':\n' + v).join('\n\n')
        : '';
      const citationsText = scmCitations.length
        ? scmCitations.map(c => c.section + ' (' + c.citation + ')').join(', ')
        : '';
      const contextParts = [
        'Current SOW Title: ' + (title || draft?.title || 'Untitled'),
        answeredWs ? 'Five Ws answered so far: ' + answeredWs : '',
        draftContentText ? 'CURRENT DRAFT CONTENT:\n' + draftContentText : '',
        citationsText ? 'SCM Citations noted: ' + citationsText : '',
      ].filter(Boolean).join('\n\n');

      const docBlocks = [];
      for (const d of supportingDocs) {
        if (d.base64) {
          docBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: d.base64 } });
        }
      }

      const priorTurns = askPennyMessages.map(m => ({ role: m.role, content: m.content }));
      const currentTurnContent = [
        ...docBlocks,
        { type: 'text', text: (contextParts ? 'CURRENT SOW CONTEXT (you have full access to this — do not say you lack it):\n' + contextParts + '\n\n' : '') + 'CM question: ' + userText },
      ];

      const pennyRaw = await callAI(apiKey, {
        system: buildAskPennySystem(agencyName),
        messages: [...priorTurns, { role: 'user', content: currentTurnContent }],
        max_tokens: 400,
        tag: 'sow_ask_penny',
      });
      const pennyMsg = { role: 'assistant', content: pennyRaw };
      setAskPennyMessages(p => [...p, pennyMsg]);
      await saveMessage('assistant', pennyRaw, 'ask_penny');
    } catch (e) {
      setAskPennyMessages(p => [...p, { role: 'assistant', content: '⚠ ' + e.message }]);
    } finally {
      setAskPennyLoading(false);
    }
  }

  // ── Handle send for main interview ────────────────────────────────────
  function handleSend() {
    if (phase === 'clarifying') return sendClarifyingAnswer();
    if (phase === 'interview') {
      if (budgetPhase) return handleBudgetAnswer();
      return sendInterviewMessage();
    }
  }

  // ── Safe JSON parse ───────────────────────────────────────────────────
  function safeParse(raw) {
    try {
      const cleaned = raw.trim().replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim();
      return JSON.parse(cleaned);
    } catch {
      return { message: raw };
    }
  }

  // ── Progress indicator ────────────────────────────────────────────────
  function getProgress() {
    if (phase === 'idea') return 0;
    if (phase === 'complete') return 100;
    if (phase === 'reviewing') return allNotesAddressed() ? 95 : 88;
    const answered = W_ORDER.filter(w => fiveWs[w]).length;
    if (phase === 'compliance' || phase === 'clarifying') return 80 + (answered / W_ORDER.length) * 5;
    return (answered / W_ORDER.length) * 75;
  }

  // ── Render ────────────────────────────────────────────────────────────

  // Initial idea screen
  if (phase === 'idea') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <Header title="New SOW Draft" refNum="" phase={phase} progress={0} saving={saving} onBack={onBack} />
        <div style={{ maxWidth: 680, margin: '48px auto', padding: '0 24px' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 36, boxShadow: '0 4px 24px rgba(0,0,0,.08)', border: '1.5px solid #e2e8f0' }}>

            {/* Penny intro */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, padding: '14px 16px', background: '#0f1f3d', borderRadius: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, color: '#0f1f3d' }}>P</div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.85)', lineHeight: 1.7 }}>
                Hi! Let's draft your Statement of Work. You can describe your idea from scratch, upload an existing SOW for Penny to auto-complete, or upload a similar SOW to use as a starting point.
              </div>
            </div>

            {/* Mode selector */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
              {[
                { key: 'describe',    icon: '✏️',  label: 'Describe from scratch',    desc: 'Tell Penny your idea' },
                { key: 'autocomplete', icon: '📄', label: 'Upload existing SOW',      desc: 'Penny auto-fills all sections' },
                { key: 'startingpoint', icon: '📋', label: 'Upload similar SOW',      desc: 'Use as a starting point' },
              ].map(opt => (
                <button key={opt.key} type="button"
                  onClick={() => setUploadMode(opt.key === 'startingpoint' ? 'upload' : opt.key === 'autocomplete' ? 'upload' : 'describe') || setUploadIntent(opt.key === 'startingpoint' ? 'startingpoint' : 'autocomplete')}
                  style={{
                    padding: '12px 10px', borderRadius: 10, border: '2px solid',
                    borderColor: (uploadMode === 'describe' && opt.key === 'describe') || (uploadMode === 'upload' && uploadIntent === opt.key) || (uploadMode === 'upload' && opt.key === 'autocomplete' && uploadIntent === 'autocomplete') || (uploadMode === 'upload' && opt.key === 'startingpoint' && uploadIntent === 'startingpoint') ? '#c49922' : '#e2e8f0',
                    background: (uploadMode === 'describe' && opt.key === 'describe') || (uploadMode === 'upload' && uploadIntent === opt.key) ? '#fffbeb' : '#f8fafc',
                    cursor: 'pointer', textAlign: 'left',
                  }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{opt.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1f3d' }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                </button>
              ))}
            </div>

            {/* Describe mode */}
            {uploadMode === 'describe' && (<>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                Describe the service or need for this SOW
              </label>
              <textarea
                value={initialIdea}
                onChange={e => setInitialIdea(e.target.value)}
                placeholder="e.g., The department needs a licensed engineering firm to conduct structural assessments of aging bridge infrastructure across 12 northern California counties. Our internal staff does not have the specialized equipment or certifications required..."
                style={{
                  width: '100%', minHeight: 140, padding: '12px 14px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit',
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                  lineHeight: 1.7, color: '#1e293b',
                }}
              />
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                Don't worry about being perfect — Penny will guide you through the Five W's.
              </div>
              <button
                onClick={createDraft}
                disabled={!initialIdea.trim() || loading || !apiKey}
                style={{
                  marginTop: 16, width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
                  background: !initialIdea.trim() || !apiKey ? '#e2e8f0' : 'linear-gradient(135deg,#c49922,#e8c04a)',
                  color: !initialIdea.trim() || !apiKey ? '#94a3b8' : '#0f1f3d',
                  fontWeight: 700, fontSize: 15, cursor: !initialIdea.trim() || !apiKey ? 'not-allowed' : 'pointer',
                  boxShadow: initialIdea.trim() && apiKey ? '0 4px 14px rgba(196,153,34,0.3)' : 'none',
                }}>
                {loading ? 'Starting with Penny…' : !apiKey ? 'API Key required — add in Settings' : '✒ Start SOW Interview with Penny →'}
              </button>
            </>)}

            {/* Upload mode */}
            {uploadMode === 'upload' && (<>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                {uploadIntent === 'autocomplete' ? 'Upload your existing SOW (PDF)' : 'Upload your sample SOW (PDF)'}
              </label>
              <div
                onClick={() => document.getElementById('sow-upload-input').click()}
                style={{
                  border: '2px dashed ' + (uploadFile ? '#16a34a' : '#e2e8f0'),
                  borderRadius: 10, padding: '28px 20px', textAlign: 'center',
                  background: uploadFile ? '#f0fdf4' : '#f8fafc', cursor: 'pointer',
                  transition: 'all .2s',
                }}>
                <input id="sow-upload-input" type="file" accept=".pdf" style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0])} />
                {uploadFile ? (
                  <>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>{uploadFile.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Click to replace</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>📤</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Click to upload PDF</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Penny will extract all sections automatically</div>
                  </>
                )}
              </div>

              {uploadIntent === 'startingpoint' && uploadFile && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#eff6ff', borderRadius: 8, border: '1.5px solid #bfdbfe', fontSize: 12.5, color: '#1d4ed8', lineHeight: 1.6 }}>
                  💡 Penny will extract what she can and flag areas you may want to modify for your specific needs.
                </div>
              )}

              <button
                onClick={createDraftFromUpload}
                disabled={!uploadFile || uploadLoading || !apiKey}
                style={{
                  marginTop: 16, width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
                  background: !uploadFile || !apiKey ? '#e2e8f0' : 'linear-gradient(135deg,#c49922,#e8c04a)',
                  color: !uploadFile || !apiKey ? '#94a3b8' : '#0f1f3d',
                  fontWeight: 700, fontSize: 15, cursor: !uploadFile || !apiKey ? 'not-allowed' : 'pointer',
                  boxShadow: uploadFile && apiKey ? '0 4px 14px rgba(196,153,34,0.3)' : 'none',
                }}>
                {uploadLoading ? 'Penny is reading your document…' : !apiKey ? 'API Key required — add in Settings' : uploadIntent === 'autocomplete' ? '📄 Extract & Auto-Complete SOW →' : '📋 Extract & Build Starting Point →'}
              </button>
            </>)}
          </div>
        </div>
      </div>
    );
  }

  // Main split-panel workspace
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f1f5f9', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Header
        title={title || draft?.title || 'SOW Draft'}
        refNum={draft?.reference_number || ''}
        phase={phase}
        progress={getProgress()}
        saving={saving}
        onBack={onBack}
        onExportPDF={() => exportToPDF(draft, draftContent, fiveWs, agencyName)}
        showExport={phase === 'complete'}
      />

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, overflow: 'hidden' }}>

        {/* ── LEFT: Penny chat ── */}
        <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1.5px solid #e2e8f0', background: '#fff', minHeight: 0 }}>

          {/* Chat header */}
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', background: '#0f1f3d', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: '#0f1f3d' }}>P</div>
            <div>
              <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>Penny</div>
              <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 10 }}>
                {phase === 'idea' ? 'Ready to start' :
                 phase === 'interview' ? `Five W's — ${W_META[currentW]?.label || 'Interview'} ${W_META[currentW]?.icon || ''}` :
                 phase === 'compliance' ? '🔍 Compliance Review' :
                 phase === 'clarifying' ? '❓ Clarifying Questions' :
                 phase === 'reviewing' ? '📋 Review & Acknowledge' :
                 '✅ SOW Complete'}
              </div>
            </div>
            {/* W progress dots */}
            {phase === 'interview' && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                {W_ORDER.map(w => (
                  <div key={w} title={W_META[w].label} style={{
                    width: 20, height: 20, borderRadius: '50%', fontSize: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: fiveWs[w] ? W_META[w].color : w === currentW ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.06)',
                    border: w === currentW ? '2px solid rgba(255,255,255,.5)' : '1px solid rgba(255,255,255,.1)',
                    color: fiveWs[w] ? '#fff' : 'rgba(255,255,255,.4)',
                    fontWeight: 700,
                  }}>{W_META[w].icon}</div>
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                {msg.role === 'assistant' && (
                  <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#0f1f3d', marginTop: 2 }}>P</div>
                )}
                <div style={{
                  maxWidth: '78%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                  background: msg.role === 'user' ? '#0f1f3d' : msg.type === 'ask_penny' ? '#f0f9ff' : msg.type === 'compliance' ? '#f0fdf4' : '#f8fafc',
                  color: msg.role === 'user' ? '#fff' : '#1e293b',
                  fontSize: 13.5, lineHeight: 1.7,
                  border: msg.role === 'user' ? 'none' : '1px solid ' + (msg.type === 'compliance' ? '#bbf7d0' : msg.type === 'ask_penny' ? '#bae6fd' : '#e2e8f0'),
                }}>
                  {msg.content.split('\n').map((line, j) => (
                    <span key={j}>
                      {line.startsWith('**') && line.endsWith('**')
                        ? <strong>{line.replace(/\*\*/g, '')}</strong>
                        : line}
                      {j < msg.content.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                  {msg.metadata?.scmCitation && (
                    <div style={{ marginTop: 6, padding: '4px 8px', background: 'rgba(196,153,34,.1)', borderRadius: 5, fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                      📖 {msg.metadata.scmCitation}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#0f1f3d' }}>P</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#c49922', animation: `bounce 1.2s ease-in-out ${i * 0.15}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area — Ask Penny always visible, interview input hidden during compliance/reviewing */}
          {phase !== 'complete' && (
            <div style={{ padding: '12px 16px', borderTop: '1.5px solid #f1f5f9', background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: phase === 'compliance' || phase === 'reviewing' ? 0 : 8 }}>
                <button
                  onClick={() => setAskPennyOpen(true)}
                  style={{
                    padding: '4px 12px', borderRadius: 99,
                    border: '1.5px solid #e2e8f0', background: '#f8fafc',
                    color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                  💬 Ask Penny a question
                  {askPennyMessages.filter(m => m.role === 'assistant').length > 0 && (
                    <span style={{ background: '#c49922', color: '#0f1f3d', borderRadius: 99, fontSize: 9, fontWeight: 800, padding: '1px 5px' }}>
                      {askPennyMessages.filter(m => m.role === 'assistant').length}
                    </span>
                  )}
                </button>
              </div>
              {/* Interview input — hidden during compliance and reviewing phases */}
              {phase !== 'compliance' && phase !== 'reviewing' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && input.trim()) handleSend(); }}
                    placeholder={
                      phase === 'clarifying' ? "Answer Penny's question…" :
                      budgetPhase ? 'Enter your estimated budget or not-to-exceed amount…' :
                      `Tell Penny about the ${W_META[currentW]?.label || 'next'} aspect…`
                    }
                    disabled={loading}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13.5, outline: 'none', fontFamily: 'inherit' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || loading}
                    style={{
                      padding: '10px 18px', borderRadius: 8, border: 'none',
                      background: !input.trim() || loading ? '#e2e8f0' : 'linear-gradient(135deg,#c49922,#e8c04a)',
                      color: !input.trim() || loading ? '#94a3b8' : '#0f1f3d',
                      fontWeight: 700, fontSize: 14, cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
                    }}>➤</button>
                </div>
              )}
            </div>
          )}

          {/* Supporting docs upload panel */}
          {docsPhase && (
            <div style={{ padding: '12px 16px', borderTop: '1.5px solid #f1f5f9', background: '#fffbeb' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 10, letterSpacing: '.04em' }}>
                📎 SUPPORTING DOCUMENTS
              </div>

              {supportingDocs.length > 0 && (
                <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {supportingDocs.map(doc => (
                    <div key={doc.id} style={{ padding: '8px 10px', borderRadius: 7, background: '#fff', border: '1px solid #fde68a', display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 14 }}>📄</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input
                          value={doc.name}
                          onChange={e => updateSupportingDoc(doc.id, { name: e.target.value })}
                          style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, fontWeight: 600, color: '#374151', outline: 'none', fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
                          <select
                            value={doc.tag}
                            onChange={e => updateSupportingDoc(doc.id, { tag: e.target.value })}
                            style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #fde68a', background: '#fffbeb', fontFamily: 'inherit' }}>
                            {['Technical Specification','Performance Standards','Process Document','User Manual','Contact List','Reference Material','Other'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#64748b', cursor: 'pointer' }}>
                            <input type="checkbox" checked={doc.includeInContract}
                              onChange={e => updateSupportingDoc(doc.id, { includeInContract: e.target.checked })} />
                            Include in contract package
                          </label>
                        </div>
                      </div>
                      <button onClick={() => removeSupportingDoc(doc.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: '0 2px', flexShrink: 0 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <label style={{ padding: '7px 12px', borderRadius: 7, border: '1.5px solid #fde68a', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#92400e' }}>
                  {docUploadLoading ? '⏳ Uploading…' : '↑ Upload Document'}
                  <input type="file" accept=".pdf" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files[0]) uploadSupportingDoc(e.target.files[0]); e.target.value = ''; }} />
                </label>
                <button
                  onClick={proceedToCompliance}
                  style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {supportingDocs.length > 0 ? `Continue with ${supportingDocs.length} document${supportingDocs.length > 1 ? 's' : ''} →` : 'Skip — No Supporting Documents →'}
                </button>
              </div>
            </div>
          )}

          {/* Export buttons when complete */}
          {phase === 'complete' && (
            <div style={{ padding: '16px', borderTop: '1.5px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => exportToPDF(draft, draftContent, fiveWs, agencyName)}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 9, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  📄 Download PDF
                </button>
                <button
                  onClick={() => alert('DOCX export coming soon — the SOW content is ready in the draft panel.')}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 9, border: '1.5px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  📝 Download DOCX
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                Share reference <strong>{draft?.reference_number}</strong> with your contract analyst
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Live SOW draft ── */}
        <div style={{ overflowY: 'auto', background: '#fafaf9' }}>
          <div style={{ padding: '20px 24px' }}>

            {/* Document header */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '.08em', marginBottom: 4 }}>STATEMENT OF WORK — EXHIBIT A</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#0f1f3d' }}>{title || draft?.title || 'Untitled SOW'}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{agencyName}</div>
                </div>
                {draft?.reference_number && (
                  <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, background: '#f1f5f9', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: 6, color: '#475569', whiteSpace: 'nowrap' }}>
                    {draft.reference_number}
                  </div>
                )}
              </div>

              {/* W progress bar */}
              <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                {W_ORDER.map(w => (
                  <div key={w} style={{ flex: 1, height: 4, borderRadius: 99, background: fiveWs[w] ? W_META[w].color : '#e2e8f0', transition: 'background .3s' }} title={W_META[w].label} />
                ))}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                {W_ORDER.filter(w => fiveWs[w]).length} of 6 sections drafted
              </div>
            </div>

            {/* SOW Sections */}
            {[
              { key: 'why',   num: 1, heading: 'BACKGROUND AND PURPOSE' },
              { key: 'what',  num: 2, heading: 'SCOPE OF SERVICES' },
              { key: 'who',   num: 3, heading: 'CONTRACTOR REQUIREMENTS' },
              { key: 'where', num: 4, heading: 'PLACE OF PERFORMANCE' },
              { key: 'when',  num: 5, heading: 'PERFORMANCE PERIOD' },
              { key: 'how',   num: 6, heading: 'PERFORMANCE STANDARDS AND METHODS' },
            ].map(sec => {
              const content = draftContent[sec.key] || fiveWs[sec.key];
              const isActive = currentW === sec.key && (phase === 'interview' || phase === 'clarifying');
              const isDone = !!fiveWs[sec.key];
              const hasCompliance = complianceNotes.some(n => n.section === sec.key);
              const complianceStatus = hasCompliance
                ? (noteStatuses[complianceNotes.findIndex(n => n.section === sec.key)] || complianceNotes.find(n => n.section === sec.key)?.status || 'open')
                : null;
              const cardBorder = isActive ? W_META[sec.key].color
                : complianceStatus === 'resolved' ? '#bbf7d0'
                : complianceStatus === 'open' ? '#fecaca'
                : complianceStatus === 'acknowledged' ? '#e2e8f0'
                : isDone ? '#e2e8f0' : '#f1f5f9';
              return (
                <div key={sec.key} style={{
                  background: '#fff', borderRadius: 10, border: '1.5px solid',
                  borderColor: cardBorder,
                  padding: '14px 18px', marginBottom: 10,
                  opacity: !isDone && !isActive ? 0.5 : 1,
                  transition: 'all .2s',
                  boxShadow: isActive ? '0 0 0 3px ' + W_META[sec.key].color + '22' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: content ? 8 : 0 }}>
                    <span style={{ fontSize: 14 }}>{W_META[sec.key].icon}</span>
                    <div style={{ fontSize: 10, fontWeight: 700, color: isDone ? W_META[sec.key].color : '#94a3b8', letterSpacing: '.06em' }}>
                      SECTION {sec.num} — {sec.heading}
                    </div>
                    {isDone && !isActive && (
                      <span style={{ marginLeft: 'auto', fontSize: 11, color:
                        complianceStatus === 'resolved' ? '#16a34a' :
                        complianceStatus === 'open' ? '#dc2626' :
                        complianceStatus === 'acknowledged' ? '#94a3b8' :
                        W_META[sec.key].color }}>
                        {complianceStatus === 'resolved' ? '✅' : complianceStatus === 'open' ? '⚠' : complianceStatus === 'acknowledged' ? '👁' : '✓'}
                      </span>
                    )}
                    {isActive && <span style={{ marginLeft: 'auto', fontSize: 10, color: W_META[sec.key].color, fontWeight: 700 }}>← answering now</span>}
                  </div>
                  {content ? (
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.75 }}>{content}</div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#cbd5e1', fontStyle: 'italic' }}>
                      {isActive ? 'Penny is waiting for your answer…' : 'Not yet drafted'}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Interactive compliance notes */}
            {complianceNotes.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1.5px solid #fde68a', padding: '14px 18px', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', letterSpacing: '.06em' }}>🔍 COMPLIANCE NOTES</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {complianceNotes.filter((_, i) => ['resolved','acknowledged'].includes(noteStatuses[i] || complianceNotes[i]?.status || 'open')).length} of {complianceNotes.length} addressed
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 99, marginBottom: 12, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 99,
                    background: 'linear-gradient(90deg, #16a34a, #22c55e)',
                    width: (complianceNotes.filter((_, i) => ['resolved','acknowledged'].includes(noteStatuses[i] || complianceNotes[i]?.status || 'open')).length / complianceNotes.length * 100) + '%',
                    transition: 'width .4s ease',
                  }} />
                </div>

                {complianceNotes.map((note, i) => {
                  const status = noteStatuses[i] || note.status || 'open';
                  const isResolved = status === 'resolved';
                  const isAcknowledged = status === 'acknowledged';
                  const isDone = isResolved || isAcknowledged;
                  const isLoading = noteLoading[i];

                  const borderColor = isResolved ? '#bbf7d0' : isAcknowledged ? '#e2e8f0' : note.severity === 'major' ? '#fecaca' : '#fde68a';
                  const bgColor = isResolved ? '#f0fdf4' : isAcknowledged ? '#f8fafc' : note.severity === 'major' ? '#fef2f2' : '#fffbeb';
                  const labelColor = isResolved ? '#16a34a' : isAcknowledged ? '#94a3b8' : note.severity === 'major' ? '#dc2626' : '#d97706';
                  const label = isResolved ? '✅ Resolved' : isAcknowledged ? '👁 Acknowledged' : note.severity === 'major' ? '⚠ Major' : '• Minor';

                  return (
                    <div key={i} style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: bgColor, border: '1.5px solid ' + borderColor, opacity: isDone ? 0.75 : 1, transition: 'all .3s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: labelColor }}>
                          {label} — {(note.section || '').toUpperCase()}
                        </div>
                        {isDone && <div style={{ marginLeft: 'auto', fontSize: 10, color: labelColor, fontWeight: 600 }}>
                          {isResolved ? 'SOW updated' : 'Analyst will review'}
                        </div>}
                      </div>
                      <div style={{ fontSize: 12, color: isDone ? '#94a3b8' : '#374151', textDecoration: isResolved ? 'line-through' : 'none' }}>{note.issue}</div>
                      {note.scmRef && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>📖 {note.scmRef}</div>}
                      {note.recommendation && !isDone && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, fontStyle: 'italic' }}>→ {note.recommendation}</div>}

                      {/* Action area — only show for open notes */}
                      {!isDone && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            <input
                              value={noteInputs[i] || ''}
                              onChange={e => setNoteInputs(p => ({ ...p, [i]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter' && (noteInputs[i] || '').trim()) resolveNote(i); }}
                              placeholder="Provide more detail to resolve this…"
                              disabled={isLoading}
                              style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: '1.5px solid ' + borderColor, fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
                            />
                            <button
                              onClick={() => resolveNote(i)}
                              disabled={!(noteInputs[i] || '').trim() || isLoading}
                              style={{
                                padding: '7px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                background: !(noteInputs[i] || '').trim() || isLoading ? '#e2e8f0' : 'linear-gradient(135deg,#c49922,#e8c04a)',
                                color: !(noteInputs[i] || '').trim() || isLoading ? '#94a3b8' : '#0f1f3d',
                              }}>
                              {isLoading ? '…' : '✓ Resolve'}
                            </button>
                          </div>
                          <button
                            onClick={() => acknowledgeNote(i)}
                            style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', width: '100%' }}>
                            👁 Acknowledge — I understand, flag for analyst review
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Export gate */}
                {(phase === 'reviewing' || phase === 'complete') && !allNotesAddressed() && (
                  <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fef9c3', border: '1.5px solid #fde047', fontSize: 12, color: '#854d0e', textAlign: 'center' }}>
                    ⚠ Please resolve or acknowledge all notes above to unlock export
                  </div>
                )}
                {(phase === 'reviewing' || phase === 'complete') && allNotesAddressed() && phase === 'reviewing' && (
                  <button
                    onClick={finalizeSOW}
                    style={{ width: '100%', marginTop: 4, padding: '11px 0', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    ✅ All notes addressed — Finalize SOW
                  </button>
                )}
              </div>
            )}

            {/* No notes — show finalize button directly */}
            {complianceNotes.length === 0 && phase === 'reviewing' && (
              <div style={{ marginBottom: 10 }}>
                <button
                  onClick={finalizeSOW}
                  style={{ width: '100%', padding: '11px 0', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  ✅ No issues found — Finalize SOW
                </button>
              </div>
            )}

            {/* Budget estimate card */}
            {estimatedBudget && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1.5px solid #fde68a', padding: '12px 16px', marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', letterSpacing: '.06em', marginBottom: 6 }}>💰 ESTIMATED BUDGET</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1f3d' }}>{estimatedBudget}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>Internal only — not included in solicitation documents</div>
              </div>
            )}

            {/* Supporting docs card */}
            {supportingDocs.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1.5px solid #e0f2fe', padding: '12px 16px', marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0284c7', letterSpacing: '.06em', marginBottom: 8 }}>📎 SUPPORTING DOCUMENTS</div>
                {supportingDocs.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 12 }}>📄</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' }}>{doc.tag}</span>
                        {doc.includeInContract && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>In contract</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Dynamic SCM references card */}
            {scmCitations.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1.5px solid #e0f2fe', padding: '14px 18px', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0284c7', letterSpacing: '.06em' }}>📖 SCM REFERENCES</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{[...new Set(scmCitations.map(c => c.citation))].length} citations</div>
                </div>

                {/* Group by section */}
                {W_ORDER.map(w => {
                  const sectionCitations = scmCitations.filter(c => c.section === w);
                  if (!sectionCitations.length) return null;
                  const noteForSection = complianceNotes.find(n => n.section === w);
                  const noteStatus = noteForSection
                    ? (noteStatuses[complianceNotes.indexOf(noteForSection)] || noteForSection.status || 'open')
                    : null;
                  const statusIcon = noteStatus === 'resolved' ? '✅' : noteStatus === 'acknowledged' ? '👁' : noteStatus === 'open' ? '⚠' : null;
                  const statusColor = noteStatus === 'resolved' ? '#16a34a' : noteStatus === 'acknowledged' ? '#94a3b8' : noteStatus === 'open' ? '#d97706' : '#0284c7';

                  return (
                    <div key={w} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 11 }}>{W_META[w].icon}</span>
                        <div style={{ fontSize: 10, fontWeight: 700, color: fiveWs[w] ? W_META[w].color : '#94a3b8', letterSpacing: '.05em' }}>
                          {W_META[w].label.toUpperCase()}
                        </div>
                        {fiveWs[w] && <div style={{ width: 6, height: 6, borderRadius: '50%', background: W_META[w].color, marginLeft: 2 }} />}
                        {statusIcon && <span style={{ marginLeft: 'auto', fontSize: 11, color: statusColor }}>{statusIcon}</span>}
                      </div>
                      {[...new Set(sectionCitations.map(c => c.citation))].map((cit, j) => (
                        <div key={j} style={{
                          fontSize: 11, color: '#374151', marginBottom: 3,
                          padding: '3px 8px', background: '#f0f9ff',
                          borderRadius: 5, borderLeft: '3px solid ' + (statusColor || '#0284c7'),
                          display: 'flex', gap: 6, alignItems: 'flex-start',
                        }}>
                          <span style={{ color: '#0284c7', flexShrink: 0 }}>→</span>
                          <span>{cit}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* Unlinked citations */}
                {scmCitations.filter(c => !c.section).length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '.05em', marginBottom: 4 }}>GENERAL</div>
                    {[...new Set(scmCitations.filter(c => !c.section).map(c => c.citation))].map((cit, i) => (
                      <div key={i} style={{ fontSize: 11, color: '#374151', marginBottom: 3, padding: '3px 8px', background: '#f0f9ff', borderRadius: 5, borderLeft: '3px solid #0284c7', display: 'flex', gap: 6 }}>
                        <span style={{ color: '#0284c7' }}>→</span> {cit}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Draft watermark */}
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 11, color: '#cbd5e1' }}>
              DRAFT — NOT FOR EXECUTION · {draft?.reference_number || ''} · GoldenScope
            </div>
          </div>
        </div>
      </div>

      {/* ── Ask Penny Slide-out Panel ── */}
      {askPennyOpen && (
        <>
          {/* Blur overlay */}
          <div
            onClick={() => setAskPennyOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(15,31,61,0.45)',
              backdropFilter: 'blur(3px)',
            }}
          />
          {/* Slide-out panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: 420, zIndex: 201,
            background: '#fff',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.2)',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Panel header */}
            <div style={{ background: '#0f1f3d', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#0f1f3d' }}>P</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Ask Penny</div>
                <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 11 }}>Ask anything — I'll answer and we'll pick up where we left off</div>
              </div>
              <button
                onClick={() => setAskPennyOpen(false)}
                style={{ background: 'rgba(255,255,255,.1)', border: 'none', borderRadius: 7, color: '#fff', fontSize: 13, fontWeight: 700, padding: '6px 12px', cursor: 'pointer' }}>
                ← Back to SOW
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
              {askPennyMessages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1f3d', marginBottom: 6 }}>Ask me anything</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                    Questions about contracting, the SCM, what a term means, or anything else on your mind. I'll answer and we'll continue your SOW right where you left off.
                  </div>
                  <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      'What is the difference between a service contract and a consulting contract?',
                      'Do I need to specify a contractor license type?',
                      'What is a DVBE and do I need one?',
                      'How specific does the place of performance need to be?',
                    ].map(q => (
                      <button key={q} onClick={() => setAskPennyInput(q)}
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {askPennyMessages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                  {msg.role === 'assistant' && (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#0f1f3d', marginTop: 2 }}>P</div>
                  )}
                  <div style={{
                    maxWidth: '82%', padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                    background: msg.role === 'user' ? '#0f1f3d' : '#f0f9ff',
                    color: msg.role === 'user' ? '#fff' : '#1e293b',
                    fontSize: 13.5, lineHeight: 1.7,
                    border: msg.role === 'user' ? 'none' : '1px solid #bae6fd',
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {askPennyLoading && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#0f1f3d' }}>P</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#c49922', animation: `bounce 1.2s ease-in-out ${i*0.15}s infinite` }} />)}
                  </div>
                </div>
              )}
              <div ref={askPennyEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '12px 16px', borderTop: '1.5px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={askPennyInput}
                  onChange={e => setAskPennyInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && askPennyInput.trim()) sendAskPenny(); }}
                  placeholder="Ask Penny anything about contracts or your SOW…"
                  disabled={askPennyLoading}
                  autoFocus
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13.5, outline: 'none', fontFamily: 'inherit' }}
                />
                <button
                  onClick={sendAskPenny}
                  disabled={!askPennyInput.trim() || askPennyLoading}
                  style={{
                    padding: '10px 18px', borderRadius: 8, border: 'none',
                    background: !askPennyInput.trim() || askPennyLoading ? '#e2e8f0' : 'linear-gradient(135deg,#c49922,#e8c04a)',
                    color: !askPennyInput.trim() || askPennyLoading ? '#94a3b8' : '#0f1f3d',
                    fontWeight: 700, fontSize: 14, cursor: !askPennyInput.trim() || askPennyLoading ? 'not-allowed' : 'pointer',
                  }}>➤</button>
              </div>
              <div style={{ marginTop: 8, textAlign: 'center' }}>
                <button onClick={() => setAskPennyOpen(false)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>
                  ← Back to SOW interview
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: .4; }
          50% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── Header component ───────────────────────────────────────────────────────
function Header({ title, refNum, phase, progress, saving, onBack, onExportPDF, showExport }) {
  const phaseLabel = {
    idea: 'Getting Started',
    interview: "Five W's Interview",
    compliance: 'Compliance Review',
    clarifying: 'Clarifying Questions',
    reviewing: 'Review & Acknowledge',
    complete: 'Complete ✅',
  }[phase] || '';

  return (
    <div style={{
      background: '#0f1f3d', padding: '0 20px', height: 52,
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 2px 12px rgba(0,0,0,.25)',
      position: 'sticky', top: 0, zIndex: 100,
    }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 6, color: 'rgba(255,255,255,.6)', fontSize: 12, padding: '5px 10px', cursor: 'pointer' }}>
        ← Back
      </button>

      <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,.1)' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title || 'SOW Draft'}
          </div>
          {refNum && (
            <div style={{ fontFamily: 'monospace', fontSize: 10, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', padding: '2px 7px', borderRadius: 4, color: 'rgba(255,255,255,.5)', whiteSpace: 'nowrap' }}>
              {refNum}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <div style={{ height: 3, width: 120, background: 'rgba(255,255,255,.1)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: progress + '%', background: 'linear-gradient(90deg,#c49922,#e8c04a)', transition: 'width .5s ease', borderRadius: 99 }} />
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)' }}>{phaseLabel}</div>
        </div>
      </div>

      {saving && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.3)' }}>Saving…</div>}

      {showExport && (
        <button onClick={onExportPDF} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#dc2626', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          📄 Export PDF
        </button>
      )}
    </div>
  );
}
